import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {getEventTitle, isAssignedTickets, isDateTbd} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect} from 'react'
import {Text, TextTypography} from 'wix-ui-tpa/cssVars'
import {hasHeadlessTYP} from '../../../../../../commons/selectors/environment'
import {isTemplate} from '../../../../../../commons/selectors/instance'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {getEvent} from '../../../selectors/event'
import {showGroupCard} from '../../../selectors/groups'
import {isEventPreview} from '../../../selectors/navigation'
import {areAllTicketsSentToBuyerEmail} from '../../../selectors/order'
import {getSuccessfulOrder, isPollEnded, isPreparingOrder} from '../../../selectors/placed-order'
import {GroupCard} from '../../group-card'
import {Spinner} from '../../spinner'
import {Container} from '../common/container'
import {Subtitle} from '../common/subtitle'
import {SuccessActions} from '../common/success-actions'
import {Title} from '../common/title'
import {LinkExpired} from '../link-expired'
import {Desktop} from './desktop'
import {Mobile} from './mobile'
import s from './order-success.scss'
import {classes} from './order-success.st.css'

export const OrderSuccess = () => {
  const {t} = useTranslation()
  const {isSSR} = useEnvironment()
  const {pollOrder, clearReservation, ticketedThankYouPageLoaded} = useAppActions()

  const event = useAppSelector(getEvent)
  const template = useAppSelector(isTemplate)
  const eventPreview = useAppSelector(isEventPreview)
  const showGroup = useAppSelector(showGroupCard)
  const pollEnded = useAppSelector(isPollEnded)
  const noExpiredLink = useAppSelector(hasHeadlessTYP)
  const order = useAppSelector(getSuccessfulOrder)

  const preparingOrder = isPreparingOrder(order)
  const eventTitle = getEventTitle(event)
  const downloadTicketsVisible = !template && !eventPreview && !preparingOrder
  const addToCalendarVisible = !isDateTbd(event)

  let ticketsSentMsg = 'ticketsThankYou.ticketSent'

  useEffect(() => {
    if (!template && !eventPreview && order) {
      ticketedThankYouPageLoaded()
      pollOrder()
      clearReservation()
    }

    focusElement({selector: hookToAttributeSelector(DH.ORDER_SUCCESS_TITLE)})
  }, [])

  if (preparingOrder && (isSSR || !pollEnded)) {
    return (
      <div className={s.spinnerContainer}>
        <Spinner diameter={100} className={classes.spinner} type="slim" />
        <Text typography={TextTypography.smallTitle}>{t('orderSuccess_loadingText')}</Text>
      </div>
    )
  }

  if (preparingOrder) {
    if (order.ticketsQuantity === 1) {
      ticketsSentMsg = 'ticketsThankYou.regProcessing.singleTicket'
    } else {
      ticketsSentMsg = 'ticketsThankYou.regProcessing.multipleTickets'
    }
  } else if (isAssignedTickets(event)) {
    if (areAllTicketsSentToBuyerEmail(order)) {
      ticketsSentMsg = 'ticketsThankYou.ticketsSentWithAssignedTickets.onlySentToBuyer'
    } else {
      ticketsSentMsg = 'ticketsThankYou.ticketsSentWithAssignedTickets'
    }
  }

  if (!order) {
    return noExpiredLink ? null : <LinkExpired />
  }

  const {firstName, email, tickets} = order
  const ticketsCount = tickets?.length ?? 0

  return (
    <Container dataHook={DH.ORDER_SUCCESS}>
      <div className={s.header}>
        <Title className={s.title} text={t('ticketsThankYou.thanks', {firstName})} dataHook={DH.ORDER_SUCCESS_TITLE} />
        <Subtitle
          lines={[t('ticketsThankYou.regComplete', {eventTitle}), t(ticketsSentMsg, {email, count: ticketsCount})]}
          dataHook={DH.ORDER_SUCCESS_SUBTITLE}
          noBottomMargin={!addToCalendarVisible && !downloadTicketsVisible}
        />
        <SuccessActions
          className={s.successActions}
          addToCalendarVisible={addToCalendarVisible}
          downloadTicketsVisible={downloadTicketsVisible}
        />
        {showGroup ? <GroupCard /> : null}
      </div>
      <div className={s.desktop} data-hook={DH.DESKTOP}>
        <Desktop />
      </div>
      <div className={s.mobile} data-hook={DH.MOBILE}>
        <Mobile />
      </div>
    </Container>
  )
}
